import React, { useMemo } from "react";
import Chart from "react-apexcharts";

/**
 * 0 Demonstração
 * 1 Plano básico
 * 2 Plano premium
 * 3 Plano avançado
 * 4 Plano platina (universidades)
 * 5 Plano diamante (universidade)
 */
const ServiceTable = [
  { title: "Demonstração", color: "secondary" },
  { title: "Plano básico", color: "success" },
  { title: "Plano premium", color: "primary" },
  { title: "Plano avançado", color: "warning" },
  { title: "Plano platina (universidades)", color: "info" },
  { title: "Plano diamante (universidade)", color: "dark" },
];

const blockStyles = `
.analytics-card{
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 12px 12px;
}
.analytics-head{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:10px;
  flex-wrap:wrap;
  margin-bottom:10px;
}
.analytics-title{
  font-weight: 900;
  font-size: .98rem;
}
.analytics-sub{
  color: var(--muted);
  font-size: .76rem;
  margin-top: 2px;
}
.kpi-row{
  display:flex;
  gap:10px;
  flex-wrap:wrap;
  margin: 10px 0 8px;
}
.kpi{
  flex: 1;
  min-width: 160px;
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 10px 10px;
  background: rgba(148,163,184,0.06);
}
.kpi-label{
  color: var(--muted);
  font-size: .74rem;
  font-weight: 750;
}
.kpi-value{
  font-weight: 950;
  font-size: 1.05rem;
  margin-top: 2px;
}
.chart-wrap{
  height: 260px;
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 10px 10px;
  background: rgba(148,163,184,0.05);
}
.chart-title{
  font-size: .78rem;
  font-weight: 850;
  color: var(--text);
  margin-bottom: 8px;
}
`;

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toDaysBucket(days) {
  if (days <= 0) return "Expirada";
  if (days < 30) return "< 30";
  if (days < 70) return "30–69";
  return ">= 70";
}

// CSS var helper (SSR safe)
const cssVar = (name, fallback) => {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

function formatInt(n) {
  return Number(n || 0).toLocaleString("pt-PT");
}

function formatCompact(n) {
  const num = Number(n || 0);
  return num.toLocaleString("pt-PT", { notation: "compact", maximumFractionDigits: 1 });
}

function planFromType(type) {
  const t = Number(type);
  if (!Number.isFinite(t)) return "Sem licença";
  const item = ServiceTable[t];
  return item?.title ?? `Plano ${t}`;
}

function pct(part, total) {
  const p = total > 0 ? (part / total) * 100 : 0;
  return `${p.toFixed(1)}%`;
}

// remove múltiplos espaços e normaliza acentos (opcional), lower-case para chave
function normalizeKey(s) {
  const raw = String(s ?? "").trim().replace(/\s+/g, " ");
  if (!raw) return "";
  // normaliza diacríticos (evita duplicados por acento diferente)
  // ex: "Huíla" vs "Huila"
  const noDiacritics = raw.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return noDiacritics.toLowerCase();
}

// title case simples para display
function toTitleCase(s) {
  const txt = String(s ?? "").trim().replace(/\s+/g, " ");
  if (!txt) return "";
  return txt
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");
}

// Normaliza província e retorna { key, label }
function normalizeProvince(state) {
  const raw = String(state ?? "Sem província").trim().replace(/\s+/g, " ");
  const safeRaw = raw || "Sem província";
  const key = normalizeKey(safeRaw) || "sem província";
  // Para “Sem província” força label fixa
  const label = key === "sem província" ? "Sem província" : toTitleCase(safeRaw);
  return { key, label };
}

function InstitutesAnalytics({ institutes = [] }) {
  const computed = useMemo(() => {
    // províncias com chave normalizada + label bonita
    const byProvinceKey = {}; // { key: count }
    const provinceLabel = {}; // { key: label }

    const byExpiry = { Expirada: 0, "< 30": 0, "30–69": 0, ">= 70": 0, "Sem licença": 0 };
    const byPlan = {};
    const smsTrend = { "0–999": 0, "1000–9999": 0, ">=10000": 0 };

    let smsTotal = 0;
    let withLicence = 0;
    let expired = 0;

    for (const inst of institutes) {
      // ---------- Províncias (resolve duplicados por case/espaço/acentos) ----------
      const { key: provKey, label: provLabel } = normalizeProvince(inst?.state);
      byProvinceKey[provKey] = (byProvinceKey[provKey] || 0) + 1;
      // guarda a label “bonita” (se existirem variações, escolhe a mais longa por segurança)
      const existing = provinceLabel[provKey];
      if (!existing || provLabel.length > existing.length) provinceLabel[provKey] = provLabel;

      // ---------- Expiração ----------
      const hasLicence = Boolean(inst?.licence);
      const days = safeNum(inst?.licence?.remaining_days);
      const expiryBucket = hasLicence ? toDaysBucket(days) : "Sem licença";
      byExpiry[expiryBucket] = (byExpiry[expiryBucket] || 0) + 1;

      // ---------- Planos ----------
      const planName = hasLicence ? planFromType(inst?.licence?.type) : "Sem licença";
      byPlan[planName] = (byPlan[planName] || 0) + 1;

      // ---------- SMS ----------
      const sms = safeNum(inst?.totalsms);
      smsTotal += sms;

      if (hasLicence) {
        withLicence += 1;
        if (days <= 0) expired += 1;
      }

      if (sms < 1000) smsTrend["0–999"] += 1;
      else if (sms < 10000) smsTrend["1000–9999"] += 1;
      else smsTrend[">=10000"] += 1;
    }

    // Top províncias (por contagem) - usando label normalizada
    const topProvince = Object.entries(byProvinceKey)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    const provinceData = topProvince.map(([key, value]) => ({
      key,
      name: provinceLabel[key] || "Sem província",
      value,
    }));

    const expiryOrder = ["Expirada", "< 30", "30–69", ">= 70", "Sem licença"];
    const expiryData = expiryOrder.map((name) => ({ name, value: byExpiry[name] || 0 }));

    const planData = Object.entries(byPlan)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }));

    const smsBands = Object.entries(smsTrend).map(([name, value]) => ({ name, value }));

    const total = institutes.length;
    const licenceRate = total > 0 ? Math.round((withLicence / total) * 100) : 0;

    return {
      provinceData,
      expiryData,
      planData,
      smsBands,
      kpis: { total, smsTotal, withLicence, expired, licenceRate },
    };
  }, [institutes]);

  const palette = useMemo(
    () => ({
      primary: "#6366f1",
      info: "#0ea5e9",
      green: "#22c55e",
      amber: "#f59e0b",
      red: "#ef4444",
      purple: "#a855f7",
      slate: "#94a3b8",
    }),
    []
  );

  const baseChartOptions = useMemo(() => {
    const text = cssVar("--text", "#0f172a");
    const muted = cssVar("--muted", "#64748b");
    const border = cssVar("--border", "rgba(148,163,184,0.25)");

    return {
      chart: {
        toolbar: { show: false },
        fontFamily: "inherit",
        foreColor: muted,
        background: "transparent",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 650,
          animateGradually: { enabled: true, delay: 120 },
          dynamicAnimation: { enabled: true, speed: 350 },
        },
      },
      grid: {
        borderColor: border,
        strokeDashArray: 4,
        padding: { left: 10, right: 10, top: 6, bottom: 6 },
      },
      tooltip: {
        theme: "dark",
        style: { fontSize: "12px" },
      },
      dataLabels: { enabled: false },
      stroke: { width: 3, lineCap: "round" },
      legend: {
        show: true,
        position: "bottom",
        fontSize: "12px",
        labels: { colors: text },
        itemMargin: { horizontal: 10, vertical: 6 },
        markers: { radius: 12 },
      },
      xaxis: {
        labels: {
          style: { fontSize: "11px" },
          trim: true,
          hideOverlappingLabels: true,
          rotate: -25,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { fontSize: "11px" },
          formatter: (v) => formatInt(v),
        },
      },
      responsive: [
        {
          breakpoint: 520,
          options: {
            xaxis: { labels: { rotate: -45 } },
            legend: { fontSize: "11px" },
          },
        },
      ],
    };
  }, []);

  // 1) Top províncias (bar) - agora SEM duplicados por case/espaço
  const provinceBar = useMemo(() => {
    const categories = computed.provinceData.map((d) => d.name);
    const values = computed.provinceData.map((d) => d.value);

    return {
      series: [{ name: "Instituições", data: values }],
      options: {
        ...baseChartOptions,
        colors: [palette.primary],
        chart: { ...baseChartOptions.chart, type: "bar" },
        plotOptions: {
          bar: {
            borderRadius: 10,
            columnWidth: "55%",
          },
        },
        fill: {
          type: "gradient",
          gradient: {
            shade: "light",
            type: "vertical",
            shadeIntensity: 0.15,
            gradientToColors: ["#8b5cf6"],
            inverseColors: false,
            opacityFrom: 0.95,
            opacityTo: 0.75,
            stops: [0, 100],
          },
        },
        grid: { ...baseChartOptions.grid, xaxis: { lines: { show: false } } },
        xaxis: { ...baseChartOptions.xaxis, categories },
        tooltip: {
          ...baseChartOptions.tooltip,
          y: { formatter: (v) => `${formatInt(v)} instituições` },
        },
      },
    };
  }, [computed.provinceData, baseChartOptions, palette.primary]);

  // 2) Expiração -> stacked 100% (barra única)
  const expiryStack100 = useMemo(() => {
    const buckets = computed.expiryData;
    const total = buckets.reduce((a, b) => a + (b.value || 0), 0);

    const getVal = (name) => buckets.find((b) => b.name === name)?.value || 0;

    const data = {
      Expirada: getVal("Expirada"),
      "< 30": getVal("< 30"),
      "30–69": getVal("30–69"),
      ">= 70": getVal(">= 70"),
      "Sem licença": getVal("Sem licença"),
    };

    const series = [
      { name: "Expirada", data: [data["Expirada"]] },
      { name: "< 30", data: [data["< 30"]] },
      { name: "30–69", data: [data["30–69"]] },
      { name: ">= 70", data: [data[">= 70"]] },
      { name: "Sem licença", data: [data["Sem licença"]] },
    ];

    const colors = [palette.red, palette.amber, palette.green, palette.primary, palette.slate];

    return {
      total,
      series,
      options: {
        ...baseChartOptions,
        colors,
        chart: {
          ...baseChartOptions.chart,
          type: "bar",
          stacked: true,
          stackType: "100%",
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 10,
            barHeight: "50%",
          },
        },
        grid: { ...baseChartOptions.grid, yaxis: { lines: { show: false } } },
        xaxis: {
          ...baseChartOptions.xaxis,
          categories: ["Licenças"],
          labels: {
            ...baseChartOptions.xaxis.labels,
            rotate: 0,
            formatter: (v) => `${Number(v).toFixed(0)}%`,
          },
          max: 100,
        },
        yaxis: {
          labels: {
            style: { fontSize: "12px", fontWeight: 900 },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (_val, opts) => {
            const name = opts.w.globals.seriesNames[opts.seriesIndex];
            const raw = data[name] ?? 0;
            if (raw <= 0) return "";
            return `${formatInt(raw)} (${pct(raw, total)})`;
          },
          style: {
            fontSize: "11px",
            fontWeight: 800,
          },
          dropShadow: { enabled: false },
        },
        tooltip: {
          ...baseChartOptions.tooltip,
          y: {
            formatter: (_value, { seriesIndex, w }) => {
              const name = w.globals.seriesNames[seriesIndex];
              const raw = data[name] ?? 0;
              return `${formatInt(raw)} • ${pct(raw, total)}`;
            },
          },
        },
      },
    };
  }, [computed.expiryData, baseChartOptions, palette]);

  // 3) Bandas SMS (area)
  const smsArea = useMemo(() => {
    const categories = computed.smsBands.map((d) => d.name);
    const values = computed.smsBands.map((d) => d.value);

    return {
      series: [{ name: "Instituições", data: values }],
      options: {
        ...baseChartOptions,
        colors: [palette.info],
        chart: { ...baseChartOptions.chart, type: "area" },
        xaxis: { ...baseChartOptions.xaxis, categories, labels: { ...baseChartOptions.xaxis.labels, rotate: 0 } },
        stroke: { curve: "smooth", width: 4 },
        markers: { size: 4, hover: { size: 6 } },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 0.25,
            opacityFrom: 0.35,
            opacityTo: 0.0,
            stops: [0, 90, 100],
          },
        },
        tooltip: {
          ...baseChartOptions.tooltip,
          y: { formatter: (v) => `${formatInt(v)} instituições` },
        },
        dropShadow: { enabled: true, top: 10, blur: 16, opacity: 0.2 },
      },
    };
  }, [computed.smsBands, baseChartOptions, palette.info]);

  // 4) Top planos (bar horizontal)
  const planBar = useMemo(() => {
    const categories = computed.planData.map((d) => d.name);
    const values = computed.planData.map((d) => d.value);

    return {
      series: [{ name: "Instituições", data: values }],
      options: {
        ...baseChartOptions,
        colors: [palette.purple],
        chart: { ...baseChartOptions.chart, type: "bar" },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 10,
            barHeight: "58%",
          },
        },
        grid: { ...baseChartOptions.grid, yaxis: { lines: { show: false } } },
        xaxis: {
          ...baseChartOptions.xaxis,
          categories,
          labels: {
            ...baseChartOptions.xaxis.labels,
            rotate: 0,
            formatter: (v) => formatInt(v),
          },
        },
        yaxis: {
          labels: {
            style: { fontSize: "12px", fontWeight: 800 },
            maxWidth: 260,
          },
        },
        tooltip: {
          ...baseChartOptions.tooltip,
          y: { formatter: (v) => `${formatInt(v)} instituições` },
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 0.2,
            opacityFrom: 0.95,
            opacityTo: 0.75,
            stops: [0, 100],
          },
        },
      },
    };
  }, [computed.planData, baseChartOptions, palette.purple]);

  return (
    <>
      <style>{blockStyles}</style>

      <div className="analytics-card col-lg-12">
        <div className="analytics-head">
          <div>
            <div className="analytics-title">Análises rápidas</div>
            <div className="analytics-sub">Resumo dos dados filtrados + gráficos para decisão rápida</div>
          </div>
        </div>

        <div className="kpi-row">
          <div className="kpi">
            <div className="kpi-label">Instituições</div>
            <div className="kpi-value">{formatInt(computed.kpis.total)}</div>
          </div>

          <div className="kpi">
            <div className="kpi-label">Instituições com licença</div>
            <div className="kpi-value">
              {formatInt(computed.kpis.withLicence)} ({computed.kpis.licenceRate}%)
            </div>
          </div>

          <div className="kpi">
            <div className="kpi-label">Licenças expiradas</div>
            <div className="kpi-value">{formatInt(computed.kpis.expired)}</div>
          </div>

          <div className="kpi">
            <div className="kpi-label">Total SMS</div>
            <div className="kpi-value">{formatCompact(computed.kpis.smsTotal)}</div>
          </div>
        </div>

        <div className="chart-wrap" style={{ marginBottom: 10 }}>
          <div className="chart-title">Top províncias (instituições)</div>
          <div style={{ height: "90%" }}>
            <Chart options={provinceBar.options} series={provinceBar.series} type="bar" height="100%" />
          </div>
        </div>

        <div className="chart-wrap" style={{ marginBottom: 10 }}>
          <div className="chart-title">Distribuição por expiração (licenças) — composição</div>
          <div style={{ height: "90%" }}>
            <Chart options={expiryStack100.options} series={expiryStack100.series} type="bar" height="100%" />
          </div>
        </div>

        <div className="chart-wrap">
          <div className="chart-title">Bandas de SMS (distribuição)</div>
          <div style={{ height: "90%" }}>
            <Chart options={smsArea.options} series={smsArea.series} type="area" height="100%" />
          </div>
        </div>
      </div>

      <div className="analytics-card" style={{ marginTop: 10 }}>
        <div className="analytics-head">
          <div>
            <div className="analytics-title">Planos mais usados</div>
            <div className="analytics-sub">Top 6 planos por quantidade (mapeado pelo ServiceTable)</div>
          </div>
        </div>

        <div className="chart-wrap">
          <div className="chart-title">Top planos</div>
          <div style={{ height: "90%" }}>
            <Chart options={planBar.options} series={planBar.series} type="bar" height="100%" />
          </div>
        </div>
      </div>
    </>
  );
}

export default InstitutesAnalytics;
